"use client";
import React, { useCallback, useEffect, useRef } from "react";

const morphTime = 1.5;
const cooldownTime = 0.5;

const useMorphingText = (texts: string[]) => {
  const textIndexRef = useRef(0);
  const morphRef = useRef(0);
  const cooldownRef = useRef(0);
  const timeRef = useRef(new Date());
  const text1Ref = useRef<HTMLSpanElement>(null);
  const text2Ref = useRef<HTMLSpanElement>(null);
  
  const setStyles = useCallback(
    (fraction: number) => {
      const [current1, current2] = [text1Ref.current, text2Ref.current];
      if (!current1 || !current2) return;
      current2.style.filter = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
      current2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;
      const invertedFraction = 1 - fraction;
      current1.style.filter = `blur(${Math.min(8 / invertedFraction - 8, 100)}px)`;
      current1.style.opacity = `${Math.pow(invertedFraction, 0.4) * 100}%`;
      current1.textContent = texts[textIndexRef.current % texts.length];
      current2.textContent = texts[(textIndexRef.current + 1) % texts.length];
    },
    [texts],
  );
  
  const doMorph = useCallback(() => {
    morphRef.current -= cooldownRef.current;
    cooldownRef.current = 0;
    let fraction = morphRef.current / morphTime;
    if (fraction > 1) {
      cooldownRef.current = cooldownTime;
      fraction = 1;
    }
    setStyles(fraction);
    if (fraction === 1) {
      textIndexRef.current++;
    }
  }, [setStyles]);
  
  const doCooldown = useCallback(() => {
    morphRef.current = 0;
    const [current1, current2] = [text1Ref.current, text2Ref.current];
    if (current1 && current2) {
      current2.style.filter = "none";
      current2.style.opacity = "100%";
      current1.style.filter = "none";
      current1.style.opacity = "0%";
    }
  }, []);
  
  useEffect(() => {
    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const newTime = new Date();
      const dt = (newTime.getTime() - timeRef.current.getTime()) / 1000;
      timeRef.current = newTime;
      cooldownRef.current -= dt;
      if (cooldownRef.current <= 0) doMorph();
      else doCooldown();
    };
    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [doMorph, doCooldown]);
  
  return { text1Ref, text2Ref };
};

const Texts = ({ texts }: { texts: string[] }) => {
  const { text1Ref, text2Ref } = useMorphingText(texts);
  return (
    <>
      <span
        className="absolute inset-0 flex items-center justify-center"
        ref={text1Ref}
      />
      <span
        className="absolute inset-0 flex items-center justify-center"
        ref={text2Ref}
      />
    </>
  );
};

const SvgFilters = () => (
  <svg id="filters" className="hidden" preserveAspectRatio="xMidYMid slice">
    <defs>
      <filter id="threshold">
        <feColorMatrix
          in="SourceGraphic"
          type="matrix"
          values="1 0 0 0 0
                  0 1 0 0 0
                  0 0 1 0 0
                  0 0 0 255 -140"
        />
      </filter>
    </defs>
  </svg>
);

const MorphingText = ({ texts, staticText, className = "" }: { texts: string[]; staticText?: string; className?: string }) => {
  // 计算最长文本的近似宽度 - 大幅增加宽度
  const maxLength = Math.max(...texts.map(text => text.length));
  const widthClass = maxLength > 8 ? "w-72" : maxLength > 6 ? "w-[365px]" : "w-56";
  
  return (
    <div className={`flex flex-row items-center justify-center ${className}`}>
      {/* Static Text */}
      {staticText && (
        <div className="relative h-16 font-sans text-4xl font-extrabold leading-none md:h-24 lg:text-[5rem] mr-4">
          <span className="inline-block">{staticText}</span>
        </div>
      )}
      
      {/* Morphing Text - 固定宽度容器 */}
      <div
        className={`relative h-16 ${widthClass} font-sans rounded-3xl text-4xl font-extrabold leading-none [filter:url(#threshold)_blur(0.1px)] md:h-24 lg:text-[5rem] border-2 border-black flex items-center justify-start pl-6 text-red-500`}
        style={{ transform: "rotate(4deg)" }}
      >
        <Texts texts={texts} />
      </div>
      
      <SvgFilters />
    </div>
  );
};

// Named export - 使用命名导出以匹配你的导入方式
export { MorphingText };